import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  approveAllProductionReviewScenes,
  approveProductionReviewScene,
  getProductionReviewScenes,
  getProductionReviewSummary,
  regenerateProductionReviewRejected,
  regenerateProductionReviewScene,
  rejectProductionReviewScene,
  selectProductionReviewSceneVersion,
  setProductionReviewScenePrompt,
  setProductionReviewSceneStatus,
  uploadProductionReviewSceneImage
} from '../services/productionReviewService';
import { requestBlob } from '../../../services/api';

function pickReviewPayload(res) {
  if (res && typeof res === 'object' && typeof res.data === 'object' && res.data != null && Array.isArray(res.data.scenes)) {
    return res.data;
  }
  if (res && typeof res === 'object' && res.success === true && typeof res.data === 'object' && res.data != null) {
    return res.data;
  }
  return res && typeof res === 'object' ? res : null;
}

function pickErrorMsg(err, fallbackMsg) {
  const preferred = err?.payload?.message || err?.payload?.error || err?.data?.message || err?.data?.error || err?.response?.data?.message || err?.message || err?.error || (err && String(err)) || fallbackMsg || 'Erro desconhecido';
  if (preferred && typeof preferred === 'string' && preferred.length <= 180) return preferred;
  return fallbackMsg || 'Falha na operação';
}

function allAssetIdsFromState(payload) {
  const ids = new Set();
  if (!payload || typeof payload !== 'object') return ids;
  (Array.isArray(payload.scenes) ? payload.scenes : []).forEach((s) => {
    if (s?.imageAssetId) ids.add(Number(s.imageAssetId));
    if (s?.videoAssetId) ids.add(Number(s.videoAssetId));
    (Array.isArray(s.versions) ? s.versions : []).forEach((v) => {
      if (v?.imageAssetId) ids.add(Number(v.imageAssetId));
      if (v?.videoAssetId) ids.add(Number(v.videoAssetId));
    });
  });
  return ids;
}

export function useProductionReview(productionId, options = {}) {
  const pid = Number(productionId || 0) || 0;
  const [loading, setLoading] = useState(false);
  const [busySceneIds, setBusySceneIds] = useState(() => new Set());
  const [reviewState, setReviewState] = useState(null);
  const [error, setError] = useState(null);
  const [versionDrawerScene, setVersionDrawerScene] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [assetPublicUrlsByAssetId, setAssetPublicUrlsByAssetId] = useState(() => new Map());
  const assetUrlCacheRef = useRef(new Map());
  const mintingRef = useRef(new Set());

  const setSceneBusy = useCallback((sceneId, busy) => {
    setBusySceneIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(Number(sceneId));
      else next.delete(Number(sceneId));
      return next;
    });
  }, []);

  const ensureAssetPublicUrls = useCallback(async (payload) => {
    const ids = [...allAssetIdsFromState(payload)].map(Number).filter((id) => id > 0);
    if (!ids.length) return;
    const pending = ids.filter((id) => !assetUrlCacheRef.current.has(id) && !mintingRef.current.has(id));
    if (!pending.length) return;
    pending.forEach((id) => mintingRef.current.add(id));
    try {
      const results = new Map();
      const chunks = [];
      for (let i = 0; i < pending.length; i += 10) chunks.push(pending.slice(i, i + 10));
      for (const chunk of chunks) {
        const part = await Promise.all(chunk.map(async (id) => {
          try {
            const blob = await requestBlob(`/api/media-assets/${id}`);
            if (blob && blob.size > 0) return [id, URL.createObjectURL(blob)];
          } catch (_) { /* erro será refletido pela ausência da mídia */ }
          return [id, null];
        }));
        part.forEach(([id, url]) => {
          if (url) {
            const previous = assetUrlCacheRef.current.get(id);
            if (previous && previous.startsWith('blob:') && previous !== url) URL.revokeObjectURL(previous);
            assetUrlCacheRef.current.set(id, url);
            results.set(id, url);
          }
        });
      }
      if (results.size) {
        setAssetPublicUrlsByAssetId((prev) => {
          const next = new Map(prev);
          results.forEach((url, id) => next.set(id, url));
          return next;
        });
      }
    } finally {
      pending.forEach((id) => mintingRef.current.delete(id));
    }
  }, []);

  const getAssetUrl = useCallback((asset, fallbackAssetId) => {
    const id = Number(asset?.id || fallbackAssetId || 0) || 0;
    if (id > 0) {
      const cached = assetUrlCacheRef.current.get(id) || assetPublicUrlsByAssetId.get(id);
      if (cached) return cached;
    }
    // URLs explícitas permanecem apenas como fallback para assets externos.
    if (typeof asset?.url === 'string' && asset.url.startsWith('http')) return asset.url;
    if (typeof asset?.publicUrl === 'string' && asset.publicUrl.startsWith('http')) return asset.publicUrl;
    if (typeof asset?.storageUrl === 'string' && asset.storageUrl.startsWith('http')) return asset.storageUrl;
    return '';
  }, [assetPublicUrlsByAssetId]);

  const applyState = useCallback((next) => {
    if (!next || typeof next !== 'object') return;
    ensureAssetPublicUrls(next).catch(() => {});
    setReviewState((cur) => {
      if (!cur) return next;
      const nextScenes = Array.isArray(next.scenes) ? next.scenes : cur.scenes;
      const merged = {
        ...cur,
        ...next,
        scenes: nextScenes,
        counts: next.counts || cur.counts,
        summary: next.summary || cur.summary
      };
      return merged;
    });
  }, [ensureAssetPublicUrls]);

  const loadReview = useCallback(async ({ silent = false } = {}) => {
    if (!pid) return null;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await getProductionReviewScenes(pid);
      const payload = pickReviewPayload(res);
      if (payload) {
        setReviewState(payload);
        ensureAssetPublicUrls(payload).catch(() => {});
      }
      if (!silent) setLoading(false);
      return payload;
    } catch (err) {
      if (!silent) setLoading(false);
      const msg = pickErrorMsg(err, 'Falha ao carregar revisão');
      setError(msg);
      if (!silent && options.toastOnError !== false) toast.error(`Erro ao carregar Central de Aprovação: ${msg}`);
      return null;
    }
  }, [pid, options.toastOnError, ensureAssetPublicUrls]);

  const loadSummary = useCallback(async () => {
    if (!pid) return null;
    try {
      const res = await getProductionReviewSummary(pid);
      return pickReviewPayload(res) || (res?.data && typeof res.data === 'object' ? res.data : null) || null;
    } catch (_) {
      return null;
    }
  }, [pid]);

  const setStatus = useCallback(async (sceneId, sceneStatus, patch) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      const res = await setProductionReviewSceneStatus(pid, sceneId, sceneStatus, patch);
      const next = pickReviewPayload(res);
      applyState(next);
      return next;
    } catch (err) {
      toast.error(pickErrorMsg(err, 'Falha ao atualizar status da cena'));
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const approveScene = useCallback(async (sceneId) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      const res = await approveProductionReviewScene(pid, sceneId);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.success('Cena aprovada');
      return next;
    } catch (err) {
      toast.error(pickErrorMsg(err, 'Falha ao aprovar cena'));
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const rejectScene = useCallback(async (sceneId) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      const res = await rejectProductionReviewScene(pid, sceneId);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.info('Cena reprovada');
      return next;
    } catch (err) {
      toast.error(pickErrorMsg(err, 'Falha ao reprovar cena'));
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const approveAll = useCallback(async () => {
    if (!pid) return null;
    setLoading(true);
    try {
      const res = await approveAllProductionReviewScenes(pid);
      const next = pickReviewPayload(res);
      applyState(next);
      if (next?.summary?.allApproved) {
        toast.success('Todas as imagens foram aprovadas. Produção pronta para montar o vídeo.');
      } else {
        toast.success('Cenas aprovadas');
      }
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao aprovar lote');
      return null;
    } finally {
      setLoading(false);
    }
  }, [pid, applyState]);

  const setPromptOverride = useCallback(async (sceneId, prompt) => {
    if (!pid) return null;
    try {
      const res = await setProductionReviewScenePrompt(pid, sceneId, prompt);
      const next = pickReviewPayload(res);
      applyState(next);
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao salvar prompt');
      return null;
    }
  }, [pid, applyState]);

  const regenerate = useCallback(async (sceneId, payload) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      toast.info('Regenerando imagem...');
      const res = await regenerateProductionReviewScene(pid, sceneId, payload);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.success('Nova versão gerada');
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao regenerar');
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const regenerateRejected = useCallback(async () => {
    if (!pid) return null;
    setLoading(true);
    try {
      toast.info('Regenerando cenas reprovadas...');
      const res = await regenerateProductionReviewRejected(pid);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.success('Regeneração concluída');
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao regenerar lote');
      return null;
    } finally {
      setLoading(false);
    }
  }, [pid, applyState]);

  const uploadManual = useCallback(async (sceneId, file, onProgress) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      const res = await uploadProductionReviewSceneImage(pid, sceneId, file, onProgress);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.success('Upload concluído');
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha no upload');
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const selectVersion = useCallback(async (sceneId, versionId) => {
    if (!pid) return null;
    setSceneBusy(sceneId, true);
    try {
      const res = await selectProductionReviewSceneVersion(pid, sceneId, versionId);
      const next = pickReviewPayload(res);
      applyState(next);
      toast.success('Versão selecionada');
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao selecionar versão');
      return null;
    } finally {
      setSceneBusy(sceneId, false);
    }
  }, [pid, applyState, setSceneBusy]);

  const isSceneBusy = useCallback((sceneId) => busySceneIds.has(Number(sceneId)), [busySceneIds]);

  useEffect(() => {
    if (pid) loadReview({ silent: false }).catch(() => {});
  }, [pid, loadReview]);

  return useMemo(() => ({
    loading,
    error,
    reviewState,
    busySceneIds,
    isSceneBusy,
    loadReview,
    loadSummary,
    setStatus,
    approveScene,
    rejectScene,
    approveAll,
    setPromptOverride,
    regenerate,
    regenerateRejected,
    uploadManual,
    selectVersion,
    versionDrawerScene,
    setVersionDrawerScene,
    previewImage,
    setPreviewImage,
    assetPublicUrlsByAssetId,
    getAssetUrl,
    ensureAssetPublicUrls
  }), [
    loading, error, reviewState, busySceneIds,
    isSceneBusy, loadReview, loadSummary, setStatus,
    approveScene, rejectScene, approveAll, setPromptOverride,
    regenerate, regenerateRejected, uploadManual, selectVersion,
    versionDrawerScene, previewImage, assetPublicUrlsByAssetId, getAssetUrl, ensureAssetPublicUrls
  ]);
}

export default useProductionReview;
